/**
 * The envelope page's v2 view (TMMAPP-0151 §G 20–22; prototype states 39–41, P:584–622): the top of
 * the page, above the existing cards and tabs.
 *
 * - **Tiles** (`V2Tile`): Balance "Across every funding account" (the served balance, with the served
 *   in / out and the reference APR under it); Budget or Goal (the served target, "{pct}% · {period}"
 *   from the served progress and the period default; the period alone when no progress is served),
 *   else Target "—" / "No target"; Planned — the sum of the listed plan lines' amounts, one
 *   occurrence each as the Planned card lists them (§G 20; TMMAPP-0154 item 12: one envelope's lines
 *   in one currency), whole and unsigned like the lines, with "{n} upcoming items · next {date}",
 *   else "—" / "Nothing planned". A failed read shows "—" with an inline error and Retry.
 * - **Last 12 months:** the served `balanceSeries` as bars (`BalanceBars`), months through
 *   `format.ts`, with a text summary.
 * - **Two columns at `lg`:** Activity — the envelope register's latest lines (date, payee, "Account
 *   ···· last4", the amount and the running balance, as served), each opening its account when the
 *   viewer owns it; then Planned — the plan lines ahead (name, next date, amount), "Planning only,
 *   never posted." — and From your Money Meetings — this envelope's items from
 *   `GET /v1/virtual-accounts/{va}/items?envelopeId=` (filtered by tmm-api), "{Kind} · {status}" over
 *   the text. Planned and the items are left out when there is nothing to show.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Box, Link, Paper, Skeleton, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { meetingsApi, type MeetingItem } from '@/api/meetings';
import type { Envelope } from '@/api/envelopes';
import { planLinesApi, type PlanLine } from '@/api/planLines';
import { registersApi, type EnvelopeRegisterLine } from '@/api/registers';
import { ErrorState } from '@/components/ErrorState';
import { MoneyParts, MoneyText } from '@/components/MoneyText';
import { TileMoney, V2Tile } from '@/components/V2Tile';
import { statusLabel } from '@/components/statusLabel';
import { BalanceBars } from '@/features/virtualAccount/BalanceBars';
import { bpToPercentText, formatCivilDate, formatMoneyMinor, formatPercent } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';

/** The Activity card's lines: the register's first page, newest first. */
const ACTIVITY_LIMIT = 8;

type Read<T> =
  { status: 'loading' } | { status: 'ready'; data: T } | { status: 'error'; error: unknown };

function useRead<T>(load: () => Promise<T>, key: string): [Read<T>, () => void] {
  const [state, setState] = useState<{ key: string; attempt: number; read: Read<T> } | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    load()
      .then((data) => {
        if (!cancelled) setState({ key, attempt, read: { status: 'ready', data } });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ key, attempt, read: { status: 'error', error } });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `load` is a fresh closure each render; the key and the attempt say when to read again
  }, [key, attempt]);
  const read: Read<T> =
    state !== null && state.key === key && state.attempt === attempt
      ? state.read
      : { status: 'loading' };
  return [read, () => setAttempt((n) => n + 1)];
}

/** A content card (visual contract v2 §6): its `h2` in the heading face, then its rows. */
function ContentCard({
  title,
  lead,
  children,
  ...rest
}: {
  title: string;
  lead?: string;
  children: ReactNode;
  'data-envelope-card'?: string;
}) {
  const tokens = resolveTokens(useTheme());
  return (
    <Paper
      component="section"
      aria-label={title}
      {...rest}
      sx={{ p: { xs: '18px 16px', sm: '20px 22px' }, display: 'flex', flexDirection: 'column' }}
    >
      <Typography variant="h3" component="h2" sx={{ fontSize: 23, lineHeight: 1.2, mb: '6px' }}>
        {title}
      </Typography>
      {lead !== undefined && (
        <Typography
          sx={{ fontSize: 13, lineHeight: '18px', color: tokens.textLabel('surface'), mb: '6px' }}
        >
          {lead}
        </Typography>
      )}
      {children}
    </Paper>
  );
}

const ROW_RULE = { borderTop: '1px solid', py: '12px' } as const;

function ActivityRows({
  lines,
  currency,
}: {
  lines: EnvelopeRegisterLine[];
  currency: string | null;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const label = tokens.textLabel('surface');
  if (lines.length === 0) {
    return (
      <Typography variant="body2" sx={{ ...ROW_RULE, borderColor: tokens.line }}>
        {t('envelope.v2.activityEmpty')}
      </Typography>
    );
  }
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {lines.map((line) => {
        const account =
          line.account.last4 !== null
            ? t('envelope.v2.accountLast4', { name: line.account.name, last4: line.account.last4 })
            : line.account.name;
        const linked = line.account.viewerOwns;
        const body = (
          <>
            <Typography
              component="span"
              sx={{ fontSize: 13, lineHeight: '20px', color: label, whiteSpace: 'nowrap' }}
            >
              {formatCivilDate(line.postedOn, locale, { month: 'short', day: 'numeric' })}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Typography
                component="span"
                data-row-title=""
                sx={{ fontWeight: 500, lineHeight: '21px', overflowWrap: 'anywhere' }}
              >
                {line.payee}
              </Typography>
              <Typography component="span" sx={{ fontSize: 12, lineHeight: '17px', color: label }}>
                {account}
              </Typography>
            </Box>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 0 }}
            >
              <Box component="span" sx={{ '& .MuiTypography-root': { fontWeight: 600 } }}>
                <MoneyText minor={line.amountMinor} currency={currency} signed tone="register" />
              </Box>
              {line.runningBalanceMinor !== null && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 12,
                    lineHeight: '17px',
                    color: label,
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <MoneyParts minor={line.runningBalanceMinor} currency={currency} />
                </Typography>
              )}
            </Box>
          </>
        );
        const rowSx = {
          display: 'grid',
          gridTemplateColumns: '52px minmax(0, 1fr) auto',
          gap: '2px 14px',
          alignItems: 'start',
          ...ROW_RULE,
          borderColor: tokens.line,
          color: tokens.text,
          textDecoration: 'none',
        } as const;
        return (
          <Box component="li" key={line.id} data-activity-row="">
            {linked ? (
              <Box
                component={RouterLink}
                to={`/me/accounts/${line.account.id}`}
                sx={{
                  ...rowSx,
                  '&:hover [data-row-title]': { textDecoration: 'underline' },
                  '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
                }}
              >
                {body}
              </Box>
            ) : (
              <Box sx={rowSx}>{body}</Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * The Planned tile's figure: the listed lines' amounts, one occurrence each. Plan lines are unsigned
 * (contract v2 §13), so lines that all go one way sum unsigned; a mix of incoming and outgoing lines
 * shows its signed net.
 */
function plannedTotal(lines: readonly PlanLine[]): number {
  const sum = lines.reduce((total, line) => total + line.amountMinor, 0);
  const oneWay =
    lines.every((line) => line.amountMinor <= 0) || lines.every((line) => line.amountMinor >= 0);
  return oneWay ? Math.abs(sum) : sum;
}

/** The plan lines still ahead, soonest first. */
function upcoming(lines: readonly PlanLine[]): PlanLine[] {
  return lines
    .filter((line) => line.nextOccurrences.length > 0)
    .sort((a, b) => (a.nextOccurrences[0] ?? '').localeCompare(b.nextOccurrences[0] ?? ''));
}

interface EnvelopeOverviewProps {
  envelope: Envelope;
  virtualAccountId: number;
  /** The virtual account OWNER's currency (§14.1). */
  currency: string | null;
}

export function EnvelopeOverview({ envelope, virtualAccountId, currency }: EnvelopeOverviewProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const id = envelope.id;
  const [activity, retryActivity] = useRead(
    () => registersApi.envelopeRegister(id, { limit: ACTIVITY_LIMIT }),
    `activity:${id}`,
  );
  const [plans, retryPlans] = useRead(() => planLinesApi.list(id), `plans:${id}`);
  const [items] = useRead(
    () => meetingsApi.listVirtualAccountItems(virtualAccountId, { envelopeId: id }),
    `items:${virtualAccountId}:${id}`,
  );

  const target = envelope.kind !== 'CASH' ? envelope.targetMinor : null;
  const ahead = plans.status === 'ready' ? upcoming(plans.data) : null;
  const next = ahead?.[0]?.nextOccurrences[0] ?? null;
  const flows = envelope.allTimeFlows;
  const tileSub = { fontSize: 12, lineHeight: '17px', color: tokens.textLabel('surface') } as const;

  const plannedTile =
    plans.status === 'error' ? (
      <V2Tile
        label={t('envelope.v2.planned')}
        value={<TileMoney minor={null} currency={null} />}
        sub={t('envelope.v2.plannedError')}
      >
        <Link
          component="button"
          type="button"
          onClick={retryPlans}
          sx={{ alignSelf: 'flex-start', fontSize: 12, fontWeight: 600 }}
        >
          {t('common.retry')}
        </Link>
      </V2Tile>
    ) : ahead === null ? (
      <V2Tile label={t('envelope.v2.planned')} value={<Skeleton width={60} />} />
    ) : ahead.length === 0 ? (
      <V2Tile
        label={t('envelope.v2.planned')}
        value={<TileMoney minor={null} currency={null} />}
        sub={t('envelope.v2.nothingPlanned')}
      />
    ) : (
      <V2Tile
        label={t('envelope.v2.planned')}
        value={<TileMoney minor={plannedTotal(ahead)} currency={currency} whole tone="total" />}
        sub={t('envelope.v2.plannedSub', {
          count: ahead.length,
          date: next !== null ? formatCivilDate(next, locale) : '',
        })}
      />
    );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }} data-envelope-overview="">
      <Box
        component="section"
        aria-label={t('envelope.v2.figures')}
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}
      >
        <V2Tile
          label={t('envelope.v2.balance')}
          value={<TileMoney minor={envelope.balanceMinor} currency={currency} />}
          sub={t('envelope.v2.balanceSub')}
        >
          {flows !== null && (
            <Typography component="span" data-envelope-flows="" sx={tileSub}>
              {t('envelope.flows', {
                in: formatMoneyMinor(flows.inMinor, currency, locale),
                out: formatMoneyMinor(Math.abs(flows.outMinor), currency, locale),
              })}
            </Typography>
          )}
          {envelope.referenceAprBp !== null && (
            <Typography component="span" data-envelope-apr="" sx={tileSub}>
              {t('envelope.aprReference', {
                apr: formatPercent(envelope.referenceAprBp / 10000, locale, {
                  maximumFractionDigits: 2,
                }),
              })}
            </Typography>
          )}
        </V2Tile>
        {target !== null ? (
          <V2Tile
            label={t(envelope.kind === 'BUDGET' ? 'envelope.v2.budget' : 'envelope.v2.goal')}
            value={<TileMoney minor={target} currency={currency} whole />}
            sub={
              // No invented progress (TMMAPP-0154 item 13): the period alone when none is served.
              envelope.progressBp === null
                ? t(`envelope.periodKind.${envelope.periodDefault.periodKind}`)
                : t('envelope.v2.targetSub', {
                    percent: bpToPercentText(envelope.progressBp, locale),
                    period: t(`envelope.periodKind.${envelope.periodDefault.periodKind}`),
                  })
            }
          />
        ) : (
          <V2Tile
            label={t('envelope.v2.target')}
            value={<TileMoney minor={null} currency={null} />}
            sub={t('envelope.v2.noTarget')}
          />
        )}
        {plannedTile}
      </Box>

      {envelope.balanceSeries.length > 0 && (
        <ContentCard title={t('envelope.v2.lastTwelve')}>
          <Box sx={{ pt: 1 }}>
            <BalanceBars
              series={envelope.balanceSeries}
              currency={currency}
              height={150}
              labels
              barRatio={0.44}
            />
          </Box>
        </ContentCard>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <ContentCard title={t('envelope.v2.activity')}>
          {activity.status === 'loading' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
              <Skeleton height={40} />
              <Skeleton height={40} />
            </Box>
          )}
          {activity.status === 'error' && (
            <ErrorState
              variant="section"
              error={activity.error}
              what={t('errors.what.envelope')}
              onRetry={retryActivity}
            />
          )}
          {activity.status === 'ready' && (
            <ActivityRows lines={activity.data.items} currency={currency} />
          )}
        </ContentCard>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {ahead !== null && ahead.length > 0 && (
            <ContentCard title={t('envelope.v2.planned')} lead={t('envelope.v2.plannedLead')}>
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {ahead.map((line) => (
                  <Box
                    component="li"
                    key={line.id}
                    data-planned-row=""
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'flex-start',
                      borderTop: '1px solid',
                      borderColor: tokens.line,
                      py: '10px',
                    }}
                  >
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <Typography
                        component="span"
                        sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}
                      >
                        {line.name ?? line.note ?? t('envelope.v2.plannedUnnamed')}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 12,
                          lineHeight: '17px',
                          color: tokens.textLabel('surface'),
                        }}
                      >
                        {formatCivilDate(line.nextOccurrences[0] ?? line.occursOn, locale)}
                      </Typography>
                    </Box>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {/* A plan line is unsigned (contract v2 §13). */}
                      <MoneyParts minor={Math.abs(line.amountMinor)} currency={currency} />
                    </Typography>
                  </Box>
                ))}
              </Box>
            </ContentCard>
          )}
          {items.status === 'ready' && items.data.length > 0 && (
            <ContentCard title={t('envelope.v2.fromMeetings')}>
              <MeetingItemRows items={items.data} />
            </ContentCard>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function MeetingItemRows({ items }: { items: MeetingItem[] }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {items.map((item) => {
        const kind = t(`items.kind.${item.kind}`);
        return (
          <Box
            component="li"
            key={item.id}
            data-meeting-item=""
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '3px',
              borderTop: '1px solid',
              borderColor: tokens.line,
              py: '10px',
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: 12, lineHeight: '17px', color: tokens.textLabel('surface') }}
            >
              {item.status !== null
                ? t('envelope.v2.itemMeta', {
                    kind,
                    status: statusLabel(t, 'meetingItem', item.status),
                  })
                : kind}
            </Typography>
            <Typography component="span" sx={{ fontSize: 14, lineHeight: '20px' }}>
              {item.body}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
